import getBatch, { type BatchExportSettings } from '@cartwhl/common/api/getBatch';
import type { MascotJobResponse } from '@cartwhl/common/api/getMascot';
import getMascotsByBatch from '@cartwhl/common/api/getMascotsByBatch';
import getMotionsByGroup, {
  type MotionRecord,
} from '@cartwhl/common/api/getMotionsByGroup';

import type {
  AssetLibraryBatchGroupAssetType,
  AssetLibraryGroupKind,
} from './assetLibraryGroupDetail';
import { fetchAllPaginated } from './assetLibraryAbortableLoader';
import { isMogenBatchGroupID } from './assetLibraryGroupIds';
import { fetchAllProjectMotions } from './assetLibraryProjectHistory';

export type ProjectMotionsCache = {
  groupID: string;
  motions: MotionRecord[];
  hasMore: boolean;
  nextToken?: string;
};

export type JobPreviewLoadInput = {
  groupID: string;
  groupKind: AssetLibraryGroupKind;
  assetType: AssetLibraryBatchGroupAssetType;
  apiKey: string;
  projectMotionsCache?: ProjectMotionsCache | null;
};

export type JobPreviewLoadResult = {
  motions: MotionRecord[];
  mascots: MascotJobResponse[];
  batchExportSettings: BatchExportSettings | null;
};

async function fetchAllMascotsByBatch(
  batchID: string,
  apiKey: string,
): Promise<MascotJobResponse[]> {
  return fetchAllPaginated({
    fetchPage: async (nextToken) => {
      const { data, error } = await getMascotsByBatch(batchID, apiKey, nextToken);
      if (error || !data) {
        throw new Error(error?.message || 'Unable to load mascot jobs.');
      }

      return {
        items: data.items ?? [],
        nextToken: data.nextToken,
      };
    },
  });
}

async function fetchAllMotionsByGroup(
  groupID: string,
  apiKey: string,
): Promise<MotionRecord[]> {
  return fetchAllPaginated({
    fetchPage: async (nextToken) => {
      const { data, error } = await getMotionsByGroup(groupID, apiKey, nextToken);
      if (error || !data) {
        throw new Error(error?.message || 'Unable to load motion jobs.');
      }

      return {
        items: data.items ?? [],
        nextToken: data.nextToken,
      };
    },
  });
}

async function resolveProjectMotions(
  groupID: string,
  cache?: ProjectMotionsCache | null,
): Promise<MotionRecord[]> {
  if (cache && cache.groupID === groupID && !cache.hasMore) {
    return cache.motions;
  }

  if (cache && cache.groupID === groupID && cache.hasMore) {
    return fetchAllProjectMotions(groupID, {
      initialMotions: cache.motions,
      initialNextToken: cache.nextToken,
    });
  }

  return fetchAllProjectMotions(groupID);
}

export async function fetchJobPreviewData(
  input: JobPreviewLoadInput,
): Promise<JobPreviewLoadResult> {
  if (input.assetType === 'MASCOT_JOB') {
    const mascots = await fetchAllMascotsByBatch(input.groupID, input.apiKey);

    return {
      mascots,
      motions: [],
      batchExportSettings: null,
    };
  }

  const motions =
    input.groupKind === 'project'
      ? await resolveProjectMotions(input.groupID, input.projectMotionsCache)
      : await fetchAllMotionsByGroup(input.groupID, input.apiKey);

  let batchExportSettings: BatchExportSettings | null = null;
  if (
    input.groupKind === 'mogen-batch' &&
    isMogenBatchGroupID(input.groupID)
  ) {
    const batchResult = await getBatch(input.groupID, input.apiKey);
    if (!batchResult.error) {
      batchExportSettings = batchResult.data?.exportSettings ?? null;
    }
  }

  return {
    motions,
    mascots: [],
    batchExportSettings,
  };
}
